import { BasicFormState } from 'hooks/useForm';
import { toInputDateTimeString } from 'utils/date-helpers';
import { AssignmentField } from './types';

const hourInMilliseconds = 60 * 60 * 1000;

const legDurationMs = (fixedHours: string, estimatedDuration: string) => {
  const fixed = Number(fixedHours);
  if (fixed > 0) {
    return fixed * hourInMilliseconds;
  }
  return Math.max(0, Math.floor(Number(estimatedDuration)) || 0);
};

const validDate = (value: string | undefined) => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/**
 * Tidsfönstret som FT-väljaren ska bedöma körtidspolicyn mot: huvudbenet plus de inlagda
 * persontransportbenen som fälttestaren själv åker (tåg/buss m.m. till och från uppdraget).
 * De sparas som egna uppdrag med samma fälttestare, men finns inte i körtidspolicyn förrän
 * formuläret sparats — utan dem kunde en FT väljas som sedan bröt mot policyn.
 *
 * Mellanlandningsbenen tas INTE med: vid en split behåller bara ett ben fälttestaren (det väljaren
 * redan räknar på), det andra planeras separat, ofta på någon annan. Därför räknas hemresan bara
 * utan mellanlandning — med mellanlandning ärver den det fälttestarlösa benet.
 *
 * Fönstret går från första benets start till sista benets slut; en väntan mellan tåg och uppdrag
 * räknas alltså in (fälttestaren är i tjänst). Ändras inget returneras huvudbenets värden oförändrade.
 */
export default function getFieldTesterPlanningWindow(
  state: BasicFormState<AssignmentField>,
  mainStart: string,
  mainDurationMs: number
): { estimatedStartTime: string; estimatedDurationMs: number } {
  const unchanged = {
    estimatedStartTime: mainStart,
    estimatedDurationMs: mainDurationMs,
  };
  const mainStartDate = validDate(mainStart);
  if (!mainStartDate) {
    return unchanged;
  }

  const legs: { start: Date; end: Date }[] = [
    {
      start: mainStartDate,
      end: new Date(mainStartDate.getTime() + Math.max(0, mainDurationMs)),
    },
  ];

  const addLeg = (start: string, fixedHours: string, estimated: string) => {
    const legStart = validDate(start);
    if (!legStart) return;
    legs.push({
      start: legStart,
      end: new Date(legStart.getTime() + legDurationMs(fixedHours, estimated)),
    });
  };

  if (
    state[AssignmentField.addingPersonalTransportationToAssignment] === 'true'
  ) {
    addLeg(
      state[
        AssignmentField.personalTransportationToAssignmentEstimatedStartTime
      ],
      state[
        AssignmentField
          .personalTransportationToAssignmentFixedAssignmentTimeInHours
      ],
      state[AssignmentField.personalTransportationToAssignmentEstimatedDuration]
    );
  }

  if (
    state[AssignmentField.addingPersonalTransportationFromAssignment] ===
      'true' &&
    state[AssignmentField.addingStopOver] !== 'true'
  ) {
    addLeg(
      state[
        AssignmentField.personalTransportationFromAssignmentEstimatedStartTime
      ],
      state[
        AssignmentField
          .personalTransportationFromAssignmentFixedAssignmentTimeInHours
      ],
      state[
        AssignmentField.personalTransportationFromAssignmentEstimatedDuration
      ]
    );
  }

  if (legs.length === 1) {
    return unchanged;
  }

  const start = Math.min(...legs.map((l) => l.start.getTime()));
  const end = Math.max(...legs.map((l) => l.end.getTime()));
  return {
    estimatedStartTime: toInputDateTimeString(new Date(start)),
    estimatedDurationMs: end - start,
  };
}
